// ═══════════════════════════════════════════════════════════
// CGB DIRECTORY — Floor & Department Data
// Civil Service & Government Development Bureau
//
// Layout source: cgbdir-hfznhhct.manus.space (original build)
// Contact source: Copy of بيانات القطاعات.xlsx (May 2026)
// ═══════════════════════════════════════════════════════════

export interface Member {
  ar: string;
  en?: string;        // English transliteration
  titleAr: string;
  titleEn?: string;   // English job title
  email?: string;
  ext?: string;
  mobile?: string;
}

export interface Department {
  id: string;
  floor: string;
  ar: string;
  en: string;
  head: boolean;
  icon: string;
  headAr: string;
  headEn: string;
  headTitle?: string;
  headTitleEn?: string;
  ext: string;
  email?: string;
  mobile?: string;
  members?: Member[];
}

export interface FloorGroup {
  floor: string;
  label: { ar: string; en: string };
  items: Department[];
}

// ─── Department definitions ────────────────────────────────
// Order follows the building: top (52) → bottom (B2)
type DeptSeed = Omit<Department, "id">;

const SEEDS: DeptSeed[] = [
  // ── Floor 52 ───────────────────────────────────────────────
  { floor: "52", ar: "المسرح", en: "Auditorium",
    head: false, icon: "▣", headAr: "", headEn: "", ext: "" },

  // ── Floor 50 — Office of the Director General ──────────────
  { floor: "50", ar: "مكتب المدير العام", en: "Office of the Director General",
    head: true, icon: "◈",
    headAr: "سيف بن علي الكعبي", headEn: "Saif bin Ali Al Kaabi",
    headTitle: "المدير العام لديوان الخدمة المدنية والتطوير الحكومي",
    headTitleEn: "Director General — Civil Service & Government Development Bureau",
    ext: "40503300", email: "SAKAABI@cgb.gov.qa", mobile: "55534664",
    members: [
      { ar: "علي الدهيمي", en: "Ali Al Duhaimi",
        titleAr: "مدير مكتب المدير العام",
        titleEn: "Director General's Office Manager",
        email: "ADEHAIMI@cgb.gov.qa", ext: "40503311", mobile: "55558440" },
    ],
  },

  // ── Floor 44 ───────────────────────────────────────────────
  { floor: "44", ar: "إدارة تخطيط القوى العاملة بالقطاع الحكومي",
    en: "Manpower Planning Department in the Government Sector",
    head: true, icon: "◎", headAr: "", headEn: "", ext: "" },

  // ── Floor 43 ───────────────────────────────────────────────
  { floor: "43", ar: "إدارة الشؤون القانونية", en: "Legal Affairs Department",
    head: true, icon: "⚖", headAr: "", headEn: "", ext: "" },

  // ── Floor 42 ───────────────────────────────────────────────
  { floor: "42", ar: "إدارة العلاقات العامة والاتصال",
    en: "Public Relations and Communication Department",
    head: true, icon: "◇", headAr: "", headEn: "", ext: "" },

  // ── Floor 39 ───────────────────────────────────────────────
  { floor: "39", ar: "إدارة الشؤون المالية والإدارية",
    en: "Financial and Administrative Affairs Department",
    head: true, icon: "▦", headAr: "", headEn: "", ext: "" },

  // ── Floor 39 — shared services sector head (Excel) ─────────
  { floor: "39", ar: "شؤون الخدمات المشتركة", en: "Shared Services Affairs",
    head: true, icon: "★",
    headAr: "مريم عسكر العمادي", headEn: "Maryam Askar Al Emadi",
    headTitle: "مدير شؤون الخدمات المشتركة",
    headTitleEn: "Director of Shared Services Affairs",
    ext: "40504555", email: "MEMADI@cgb.gov.qa", mobile: "55008006",
    members: [
      { ar: "حمده احمد عبدالله المحمد", en: "Hamda Ahmed Abdullah Al Mohammed",
        titleAr: "مدير مكتب مدير شؤون الخدمات المشتركة",
        titleEn: "Office Director — Shared Services Affairs",
        email: "HAMOHAMMED@cgb.gov.qa", ext: "40504725", mobile: "77128932" },
    ],
  },

  // ── Floor 37 ───────────────────────────────────────────────
  { floor: "37", ar: "إدارة الموارد البشرية", en: "Human Resource Department",
    head: true, icon: "♦", headAr: "", headEn: "", ext: "" },

  // ── Floor 35 — Government Services Performance Planning ────
  { floor: "35", ar: "إدارة تخطيط ومتابعة أداء الخدمات الحكومية",
    en: "Government Services Performance Planning and Followup Department",
    head: true, icon: "◎",
    headAr: "نورا خالد منصور", headEn: "Nora Khalid Mansour",
    headTitle: "مدير الإدارة",
    headTitleEn: "Department Director",
    ext: "40505111", email: "NMANSOUR@cgb.gov.qa", mobile: "66684044",
    members: [
      { ar: "شيخة محمد المالكي", en: "Sheikha Mohammed Al Maliki",
        titleAr: "رئيس قسم تقييم الخدمات والإجراءات الحكومية",
        titleEn: "Head of Government Services & Procedures Assessment",
        email: "SMIMALKI@cgb.gov.qa", ext: "40505105", mobile: "55970696" },
      { ar: "نوره محمد على آل خليفه", en: "Noura Mohammed Ali Al Khalifa",
        titleAr: "رئيس قسم تخطيط وتطوير الخدمات الحكومية",
        titleEn: "Head of Government Services Planning & Development",
        email: "NALKHALIFA@cgb.gov.qa", ext: "40505055", mobile: "30588808" },
    ],
  },

  // ── Floor 34 — Information Systems ─────────────────────────
  { floor: "34", ar: "إدارة نظم المعلومات", en: "Information Systems Department",
    head: true, icon: "⬡",
    headAr: "فاطمه عبدالعزيز الخرجي", headEn: "Fatima Abdulaziz Al Kharji",
    headTitle: "مدير إدارة نظم المعلومات",
    headTitleEn: "Director of Information Systems Department",
    ext: "40504999", email: "FKHARJI@cgb.gov.qa", mobile: "31100699",
    members: [
      { ar: "حصه محمد رومى النعيمي", en: "Hessa Mohammed Rumi Al Naimi",
        titleAr: "رئيس قسم أمن المعلومات",
        titleEn: "Head of Information Security",
        email: "HMNAIMI@cgb.gov.qa", ext: "40504992", mobile: "66228983" },
      { ar: "فاطمه عبدالرحمن البدر", en: "Fatima Abdulrahman Al Bader",
        titleAr: "رئيس قسم", titleEn: "Section Head",
        email: "FBADER@cgb.gov.qa", ext: "40504993" },
      { ar: "مريم عبدالله يوسف", en: "Maryam Abdullah Yousef",
        titleAr: "رئيس قسم التطوير وإدارة المشاريع",
        titleEn: "Head of Development & Project Management",
        email: "MAABDULLA@cgb.gov.qa", ext: "40504990" },
      { ar: "هدى عبدالله الشافعي", en: "Huda Abdullah Al Shafei",
        titleAr: "رئيس قسم البنية التحتية",
        titleEn: "Head of Infrastructure",
        email: "HSHAFAI@cgb.gov.qa", ext: "40504991" },
    ],
  },

  // ── Floor 33 — Organization Affairs (Excel-only sector) ────
  { floor: "33", ar: "شؤون التنظيم", en: "Organization Affairs",
    head: true, icon: "◈",
    headAr: "عبد الله مسلم الدوسري", headEn: "Abdullah Muslim Al Dosari",
    headTitle: "مدير شؤون التنظيم",
    headTitleEn: "Director of Organization Affairs",
    ext: "40504333", email: "Adosari@cgb.gov.qa", mobile: "55880747",
    members: [
      { ar: "ناصر سالم السبيعي", en: "Nasser Salem Al Subaie",
        titleAr: "مدير مكتب مدير شؤون التنظيم",
        titleEn: "Office Director — Organization Affairs",
        email: "NSUBAEY@cgb.gov.qa", ext: "40504334", mobile: "55554905" },
    ],
  },

  // ── Floor 32 — HR Information Systems Center ───────────────
  { floor: "32", ar: "مركز نظم معلومات الموارد البشرية",
    en: "Human Resource Information Systems Center",
    head: true, icon: "◉",
    headAr: "حسن الخاجة", headEn: "Hassan Al Khaja",
    headTitle: "مدير مركز نظم معلومات الموارد البشرية",
    headTitleEn: "Director of HR Information Systems Center",
    ext: "40505999", email: "HKHAJA@cgb.gov.qa", mobile: "77477400",
    members: [
      { ar: "موسى محمد الدراوشة", en: "Mousa Mohammed Al Darawsheh",
        titleAr: "مدير برامج أول", titleEn: "Senior Programs Manager",
        email: "MDARAWSHEH@cgb.gov.qa", ext: "40505917", mobile: "33743664" },
      { ar: "بخيته سالم العجمي", en: "Bukhita Salem Al Ajmi",
        titleAr: "رئيس قسم الخدمات المساندة والدعم الفني",
        titleEn: "Head of Support Services & Technical Support",
        email: "BAJMI@cgb.gov.qa", ext: "40505905", mobile: "55078228" },
      { ar: "روضة عبداللطيف الموسى", en: "Rawdha Abdullatif Al Mousa",
        titleAr: "رئيس قسم المشاريع والتحول الرقمي",
        titleEn: "Head of Projects & Digital Transformation",
        email: "RMUSA@cgb.gov.qa", ext: "40505926", mobile: "33411411" },
      { ar: "شيخه خميس الخليفي", en: "Sheikha Khamis Al Khulaifi",
        titleAr: "رئيس قسم تطبيقات التطوير",
        titleEn: "Head of Development Applications",
        email: "SKKHULAIFI@cgb.gov.qa", ext: "40505938", mobile: "55577545" },
      { ar: "موزا غانم الهاجري", en: "Moza Ghanim Al Hajri",
        titleAr: "رئيس قسم تحليل البيانات الرقمية",
        titleEn: "Head of Digital Data Analysis",
        email: "MGHAJRI@cgb.gov.qa", ext: "40505919", mobile: "66550066" },
    ],
  },

  // ── Floor 31 ───────────────────────────────────────────────
  { floor: "31", ar: "إدارة سياسات الموارد البشرية",
    en: "Human Resources Policy Department",
    head: true, icon: "♦", headAr: "", headEn: "", ext: "" },

  // ── Floor 29 ───────────────────────────────────────────────
  { floor: "29", ar: "إدارة الترشيح والإحلال",
    en: "Nomination and Replacement Department",
    head: true, icon: "◎", headAr: "", headEn: "", ext: "" },

  // ── Floor 27 — Civil Service Affairs ───────────────────────
  { floor: "27", ar: "شؤون الخدمة المدنية", en: "Civil Service Affairs",
    head: true, icon: "⚖",
    headAr: "يعقوب آل إسحاق", headEn: "Yacoub Al Ishaq",
    headTitle: "مدير شؤون الخدمة المدنية",
    headTitleEn: "Director of Civil Service Affairs",
    ext: "40505500", email: "Ysaleh@cgb.gov.qa", mobile: "55997996",
    members: [
      { ar: "عبدالله فرج قحطان السليطي", en: "Abdullah Faraj Qahtan Al Sulaiti",
        titleAr: "مدير مكتب مدير شؤون الخدمة المدنية",
        titleEn: "Office Director — Civil Service Affairs",
        email: "Afsulaiti@cgb.gov.qa", ext: "40505505", mobile: "66415562" },
    ],
  },

  // ── Floor 26 ───────────────────────────────────────────────
  { floor: "26", ar: "مخزن مولدات الطاقة", en: "Energy Generators Store",
    head: false, icon: "▦", headAr: "", headEn: "", ext: "" },

  // ── Floor 25 ───────────────────────────────────────────────
  { floor: "25", ar: "إدارة الدراسات التنظيمية",
    en: "Department of Organizational Studies",
    head: true, icon: "◈", headAr: "", headEn: "", ext: "" },

  // ── Floor 24 ───────────────────────────────────────────────
  { floor: "24", ar: "إدارة الهياكل التنظيمية وترتيب الوظائف",
    en: "Managing Organizational Structures and Job Arrangement",
    head: true, icon: "▤", headAr: "", headEn: "", ext: "" },

  // ── Floor 23 ───────────────────────────────────────────────
  { floor: "23", ar: "إدارة الشؤون المالية والإدارية - المركز الفني",
    en: "Financial and Administrative Affairs - Technical Center",
    head: true, icon: "▦", headAr: "", headEn: "", ext: "" },

  // ── Floor 22 ───────────────────────────────────────────────
  { floor: "22", ar: "إدارة المواهب وتنمية الموارد البشرية الوطنية",
    en: "Talent Management and National Human Resources Development",
    head: true, icon: "♦", headAr: "", headEn: "", ext: "" },

  // ── Floor 19 — Government Buildings Department (Excel) ─────
  { floor: "19", ar: "إدارة المباني الحكومية", en: "Government Buildings Department",
    head: true, icon: "▤",
    headAr: "ناصر النعيمي", headEn: "Nasser Al Naimi",
    headTitle: "مدير إدارة",
    headTitleEn: "Department Director",
    ext: "40504222", email: "NNALNAIMI@cgb.gov.qa", mobile: "55022633",
    members: [
      { ar: "صالح المهندي", en: "Saleh Al Muhannadi",
        titleAr: "رئيس قسم", titleEn: "Section Head",
        email: "SBOMATAR@cgb.gov.qa", ext: "40504203", mobile: "55511836" },
      { ar: "أحمد المنصوري", en: "Ahmed Al Mansoori",
        titleAr: "رئيس قسم", titleEn: "Section Head",
        email: "AMANSORI@cgb.gov.qa", ext: "40504155", mobile: "55636262" },
      { ar: "عبدالرحمن الزيني", en: "Abdulrahman Al Zaini",
        titleAr: "رئيس قسم", titleEn: "Section Head",
        email: "AALZAINI@cgb.gov.qa", ext: "40504154", mobile: "55514646" },
    ],
  },

  // ── Floor 17 — Government Housing Department (Excel) ───────
  { floor: "17", ar: "إدارة الإسكان الحكومي", en: "Government Housing Department",
    head: true, icon: "▦",
    headAr: "جاسم تلفت", headEn: "Jassim Telefat",
    headTitle: "مدير إدارة الإسكان الحكومي",
    headTitleEn: "Director of Government Housing Department",
    ext: "40504000", email: "JTELEFAT@cgb.gov.qa", mobile: "30000694" },

  // ── Floor 16 — Government Service Centers ──────────────────
  { floor: "16", ar: "إدارة مراكز الخدمات الحكومية",
    en: "Government Service Centers Department",
    head: true, icon: "☎",
    headAr: "منيرة انديلة", headEn: "Munira Andeela",
    headTitle: "مدير إدارة",
    headTitleEn: "Department Director",
    ext: "40505222", email: "MANDEELA@cgb.gov.qa", mobile: "70008299",
    members: [
      { ar: "عمر احمد المحمد السالم", en: "Omar Ahmed Al Mohammed Al Salem",
        titleAr: "رئيس قسم", titleEn: "Section Head",
        email: "OSALEM@cgb.gov.qa", ext: "40505224", mobile: "77677899" },
      { ar: "امل صالح قاسم اليافعي", en: "Amal Saleh Qasem Al Yafei",
        titleAr: "رئيس قسم", titleEn: "Section Head",
        email: "ayafei@cgb.gov.qa", ext: "40505227", mobile: "66644401" },
    ],
  },

  // ── Floor 15 — Government Services Performance (planning) ──
  { floor: "15", ar: "إدارة تخطيط ومتابعة أداء الخدمات الحكومية",
    en: "Gov. Services Performance Planning & Followup Dept.",
    head: true, icon: "◎", headAr: "", headEn: "", ext: "" },

  // ── Floor 15 — Office of Government Development Affairs ────
  { floor: "15", ar: "مكتب مدير شؤون التطوير الحكومي",
    en: "Office of the Director of Government Development Affairs",
    head: true, icon: "★",
    headAr: "م. يوسف جابر الجابر", headEn: "Eng. Yousef Jaber Al Jaber",
    headTitle: "مدير شؤون التطوير الحكومي",
    headTitleEn: "Director of Government Development Affairs",
    ext: "40505000", email: "yaljaber@cgb.gov.qa", mobile: "55004944",
    members: [
      { ar: "عزة عبدالله العلي", en: "Azza Abdullah Al Ali",
        titleAr: "مدير مكتب شؤون التطوير الحكومي",
        titleEn: "Office Director — Government Development Affairs",
        email: "aali@cgb.gov.qa", ext: "40503672", mobile: "55029997" },
    ],
  },

  // ── Floor 14 — Government Innovation Department ────────────
  { floor: "14", ar: "إدارة الابتكار الحكومي", en: "Government Innovation Department",
    head: true, icon: "◈",
    headAr: "حصة التميمي", headEn: "Hessa Al Tamimi",
    headTitle: "مدير إدارة الابتكار الحكومي",
    headTitleEn: "Director of Government Innovation Department",
    ext: "40505100", email: "htamimi@cgb.gov.qa", mobile: "77990787",
    members: [
      { ar: "فاطمة صقر النعيمي", en: "Fatima Saqr Al Naimi",
        titleAr: "رئيس قسم مختبر الابتكار",
        titleEn: "Head of Innovation Lab",
        email: "fnaemi@cgb.gov.qa", ext: "40505109", mobile: "55755202" },
    ],
  },

  // ── Floor 14 — Government Service Excellence Department ────
  { floor: "14", ar: "إدارة تميز الخدمات الحكومية",
    en: "Government Service Excellence Department",
    head: true, icon: "◇",
    headAr: "وضحى سلطان البادي", headEn: "Wadha Sultan Al Badi",
    headTitle: "المكلف بمهام مدير إدارة التميز الحكومي",
    headTitleEn: "Acting Director of Government Excellence Department",
    ext: "40505061", email: "wmaadeed@cgb.gov.qa", mobile: "55814437",
    members: [
      { ar: "منيرة الكواري", en: "Munira Al Kuwari",
        titleAr: "رئيس قسم تصميم وتطوير الإجراءات الحكومية",
        titleEn: "Head of Government Procedures Design & Development",
        email: "mmalkuwari@cgb.gov.qa", ext: "40505052", mobile: "55570750" },
      { ar: "إيمان الشمري", en: "Iman Al Shammari",
        titleAr: "المكلف بمهام رئيس قسم تخطيط وتطوير التميز",
        titleEn: "Acting Head of Excellence Planning & Development",
        email: "ealshamari@cgb.gov.qa", ext: "40505051", mobile: "55965575" },
    ],
  },

  // ── Floor 12 — Planning, Quality & Innovation ──────────────
  { floor: "12", ar: "إدارة التخطيط والجودة والابتكار",
    en: "Planning, Quality and Innovation Department",
    head: true, icon: "▲",
    headAr: "د. نوف عبدالله الربيعه الكعبي", headEn: "Dr. Nouf Abdullah Al Kaabi",
    headTitle: "مدير إدارة التخطيط والجودة والابتكار",
    headTitleEn: "Director of Planning, Quality & Innovation Department",
    ext: "40503222", email: "NAKAABI@cgb.gov.qa", mobile: "55853168",
    members: [
      { ar: "عائشه سلطان الكواري", en: "Aisha Sultan Al Kuwari",
        titleAr: "رئيس قسم التخطيط الاستراتيجي",
        titleEn: "Head of Strategic Planning",
        email: "ASKUWARI@cgb.gov.qa", ext: "40503288", mobile: "66826668" },
      { ar: "خديجه سالم باشراحيل", en: "Khadija Salem Bashrahil",
        titleAr: "رئيس مكتب البنية المؤسسية",
        titleEn: "Head of Institutional Architecture Office",
        email: "KBSHRAHIL@cgb.gov.qa", ext: "40503252", mobile: "55770167" },
      { ar: "صباح فهد العنزي", en: "Sabah Fahad Al Enazi",
        titleAr: "المكلف بمهام رئيس قسم الجودة والتطوير والابتكار",
        titleEn: "Acting Head of Quality, Development & Innovation",
        email: "SENAZI@cgb.gov.qa", ext: "40503263", mobile: "55150220" },
    ],
  },

  // ── Floor 10 — Office of the President (placeholder) ───────
  { floor: "10", ar: "مكتب سعادة الرئيس", en: "Office of the President",
    head: true, icon: "★", headAr: "", headEn: "", ext: "" },

  // ── Floor 10 — Institute of Public Administration (Excel) ──
  { floor: "10", ar: "معهد الإدارة العامة", en: "Institute of Public Administration",
    head: true, icon: "◉",
    headAr: "مها مسعود المري", headEn: "Maha Masoud Al Marri",
    headTitle: "مدير معهد الإدارة العامة",
    headTitleEn: "Director of the Institute of Public Administration",
    ext: "40503666", email: "MMMARRI@cgb.gov.qa", mobile: "77700606",
    members: [
      { ar: "بلال الشمالي", en: "Bilal Al Shamali",
        titleAr: "القائم بمهام مدير مكتب مدير معهد الإدارة",
        titleEn: "Acting Office Director — Institute of Public Administration",
        email: "BALSHAMALI@cgb.gov.qa", ext: "40503818", mobile: "70777927" },
    ],
  },

  // ── Floor 9 — Office of His Excellency the President ───────
  { floor: "09", ar: "مكتب سعادة الرئيس", en: "Office of the President",
    head: true, icon: "★",
    headAr: "د. عبدالعزيز بن ناصر بن مبارك الخليفة",
    headEn: "HE Dr. Abdulaziz bin Nasser bin Mubarak Al Khalifa",
    headTitle: "رئيس ديوان الخدمة المدنية والتطوير الحكومي",
    headTitleEn: "President of the Civil Service & Government Development Bureau",
    ext: "40503000", email: "AALKHALIFA@cgb.gov.qa", mobile: "55444844",
    members: [
      { ar: "عبد الرحمن فوزي الخميس", en: "Abdulrahman Fawzi Al Khamis",
        titleAr: "مدير مكتب رئيس ديوان الخدمة المدنية والتطوير الحكومي",
        titleEn: "Director — Office of the CGB President",
        email: "AALKhamis@cgb.gov.qa", ext: "40503001", mobile: "55527233" },
    ],
  },

  // ── Floor 8 — Internal Audit Department ────────────────────
  { floor: "08", ar: "إدارة التدقيق الداخلي", en: "Internal Audit Department",
    head: true, icon: "◉", headAr: "", headEn: "", ext: "" },
  { floor: "08", ar: "المكتب الفني", en: "Technical Office",
    head: true, icon: "⚙", headAr: "", headEn: "", ext: "" },

  // ── MF — Government Innovation Laboratory ──────────────────
  { floor: "MF", ar: "مختبر الابتكار الحكومي",
    en: "Government Innovation Laboratory",
    head: true, icon: "◈", headAr: "", headEn: "", ext: "" },

  // ── GF — Reception ─────────────────────────────────────────
  { floor: "GF", ar: "الاستقبال", en: "Reception",
    head: false, icon: "◆", headAr: "", headEn: "", ext: "" },

  // ── B1 / B2 — Parking ──────────────────────────────────────
  { floor: "B1", ar: "مواقف السيارات", en: "Parking",
    head: false, icon: "▨", headAr: "", headEn: "", ext: "" },
  { floor: "B2", ar: "مواقف السيارات", en: "Parking",
    head: false, icon: "▨", headAr: "", headEn: "", ext: "" },
];

// ─── Full floor inventory (52 → B2) ────────────────────────
const ALL_FLOOR_IDS = [
  "52", "51", "50", "49", "48", "47", "46", "45", "44", "43", "42", "41", "40",
  "39", "38", "37", "36", "35", "34", "33", "32", "31", "30",
  "29", "28", "27", "26", "25", "24", "23", "22", "21", "20",
  "19", "18", "17", "16", "15", "14", "13", "12", "11", "10",
  "09", "08", "07", "06", "05", "04", "03", "02", "01",
  "MF", "GF", "B1", "B2",
];

export const ALL: Department[] = SEEDS.map((s, i) => ({
  id: `D${i.toString().padStart(3, "0")}`,
  ...s,
}));

// Empty placeholders for floors with no listed department
ALL_FLOOR_IDS.forEach((f) => {
  if (!ALL.some((d) => d.floor === f)) {
    ALL.push({
      id: `E${f}`, floor: f, ar: "", en: "",
      head: false, icon: "◆", headAr: "", headEn: "", ext: "",
    });
  }
});

export const NAMED = ALL.filter((d) => d.ar && d.head);
export const TOTAL_FLOORS = ALL_FLOOR_IDS.length;

export function floorLabel(f: string): { ar: string; en: string } {
  if (f === "GF") return { ar: "الطابق الأرضي", en: "Ground Floor" };
  if (f === "MF") return { ar: "الميزانين", en: "Mezzanine" };
  if (f === "B1") return { ar: "بدروم ١", en: "Basement 1" };
  if (f === "B2") return { ar: "بدروم ٢", en: "Basement 2" };
  return { ar: "الطابق " + parseInt(f), en: "Floor " + parseInt(f) };
}

export const SORTED_FLOORS: FloorGroup[] = (() => {
  const grouped: Record<string, Department[]> = {};
  ALL.forEach((d) => {
    if (!grouped[d.floor]) grouped[d.floor] = [];
    grouped[d.floor].push(d);
  });
  return ALL_FLOOR_IDS.slice() // 52 → B2 (top-down)
    .map((f) => ({ floor: f, label: floorLabel(f), items: grouped[f] || [] }))
    .filter((g) => g.items.length > 0);
})();

// ─── Arabic-aware text normalization ──────────────────────
// Folds diacritics, alef/yaa/ta-marbuta variants, tatweel, and honorifics
// so search tolerates: partial names, missing middle names, common spellings.
function normalize(s: string | undefined): string {
  if (!s) return "";
  return s
    .toLowerCase()
    // Strip Arabic diacritics (tashkeel) + dagger-alef
    .replace(/[ً-ٰٟ]/g, "")
    // Strip tatweel (kashida)
    .replace(/ـ/g, "")
    // Normalize Alef variants → ا
    .replace(/[آأإ]/g, "ا")
    // Normalize Alef-Maksura → Yaa
    .replace(/ى/g, "ي")
    // Normalize Ta-Marbuta → Haa (loose matching)
    .replace(/ة/g, "ه")
    // Strip Hamza on Waw / Yaa
    .replace(/ؤ/g, "و")
    .replace(/ئ/g, "ي")
    // Punctuation → spaces (handles "د." "م." commas, etc.)
    .replace(/[.,،:;!؟?'"()\[\]]/g, " ")
    // Collapse whitespace
    .replace(/\s+/g, " ")
    .trim();
}

// Arabic honorifics & particles to ignore when present alone
const NOISE = new Set([
  "د", "م", "أ", "ا",
  "الدكتور", "الدكتوره",
  "المهندس", "المهندسه",
  "السيد", "السيده",
  "الشيخ", "الشيخه",
  "سعاده", "سعادة",
  "dr", "eng", "mr", "mrs", "ms",
]);

// Match query against a single haystack string. Strategy:
//   1. exact substring (after normalization)
//   2. exact substring with all whitespace removed
//   3. every meaningful token from query must appear in haystack
function matchQuery(query: string, haystack: string): boolean {
  const q = normalize(query);
  const h = normalize(haystack);
  if (!q) return false;
  if (h.includes(q)) return true;
  const qNoSp = q.replace(/\s/g, "");
  const hNoSp = h.replace(/\s/g, "");
  if (qNoSp.length >= 2 && hNoSp.includes(qNoSp)) return true;

  const tokens = q.split(" ").filter((tok) => tok.length > 0 && !NOISE.has(tok));
  if (tokens.length === 0) return false;
  return tokens.every((tok) => h.includes(tok));
}

export function doSearch(q: string, mode: string): Department[] {
  if (!q || !q.trim()) return [];
  const raw = q.trim();
  const t = raw.toLowerCase();

  // ─── Floor shortcuts (numeric or named) ─────────────────
  const aliases: Record<string, string> = {
    g: "GF", gf: "GF", ground: "GF",
    "أرضي": "GF", "ارضي": "GF",
    mf: "MF", mezzanine: "MF", "ميزانين": "MF",
    b1: "B1", b2: "B2", "بدروم": "B1", basement: "B1",
  };

  if (mode === "floor" || (mode === "all" && (aliases[t] || t.match(/^\d{1,2}$/)))) {
    if (aliases[t]) return ALL.filter((d) => d.floor === aliases[t] && d.ar);
    const p = t.padStart(2, "0");
    const m = ALL.filter((d) => (d.floor === p || d.floor === t) && d.ar);
    if (m.length) return m;
    const arabicNums: Record<string, string> = { "٠":"0","١":"1","٢":"2","٣":"3","٤":"4","٥":"5","٦":"6","٧":"7","٨":"8","٩":"9" };
    const cv = t.split("").map((c) => arabicNums[c] || c).join("");
    if (cv !== t && cv.match(/^\d{1,2}$/)) {
      const m2 = ALL.filter((d) => (d.floor === cv.padStart(2, "0") || d.floor === cv) && d.ar);
      if (m2.length) return m2;
    }
  }

  // ─── Field-based search per mode ────────────────────────
  return ALL.filter((d) => {
    if (!d.ar) return false;
    let fields: string[];
    if (mode === "dept") {
      fields = [d.ar, d.en];
    } else if (mode === "head") {
      fields = [d.headAr, d.headEn, d.headTitle || "", d.headTitleEn || ""];
      d.members?.forEach((m) => fields.push(m.ar, m.en || "", m.titleAr, m.titleEn || ""));
    } else {
      fields = [
        d.ar, d.en, d.headAr, d.headEn,
        d.headTitle || "", d.headTitleEn || "",
        d.floor, floorLabel(d.floor).ar, floorLabel(d.floor).en,
      ];
      if (d.email) fields.push(d.email);
      if (d.ext) fields.push(d.ext);
      if (d.mobile) fields.push(d.mobile);
      d.members?.forEach((m) => {
        fields.push(m.ar, m.en || "", m.titleAr, m.titleEn || "");
        if (m.email) fields.push(m.email);
        if (m.ext) fields.push(m.ext);
        if (m.mobile) fields.push(m.mobile);
      });
    }
    return matchQuery(raw, fields.join(" "));
  });
}
